// Regime classification and playbook data sourced from docs/market_regime_playbook.md §4.1-§4.6, §6.1

export type MarketRegime = 'STRONG_BULL' | 'WEAK_BULL' | 'NEUTRAL' | 'WEAK_BEAR' | 'STRONG_BEAR' | 'VOLATILE'

export function deriveMarketRegime(trendScore: number, volatilityRegime: number): MarketRegime {
  if (volatilityRegime > 0.65) return 'VOLATILE'
  if (trendScore > 0.6)  return 'STRONG_BULL'
  if (trendScore > 0.15) return 'WEAK_BULL'
  if (trendScore >= -0.15) return 'NEUTRAL'
  if (trendScore >= -0.6)  return 'WEAK_BEAR'
  return 'STRONG_BEAR'
}

export const REGIME_OVERVIEW: Record<MarketRegime, { label: string; description: string; tagline: string; color: string }> = {
  STRONG_BULL: {
    label: 'Strong Bull',
    description: 'Trend up + sentiment > 0.7 + volume confirming',
    tagline: 'Maximize exposure with aggressive LP and loops. Minimal hedging — let the trend work.',
    color: 'bg-accent-green/20 text-accent-green',
  },
  WEAK_BULL: {
    label: 'Weak Bull',
    description: 'Trend up + mixed signals',
    tagline: 'Maintain moderate exposure with partial hedging. Conservative loops, moderate LP ranges.',
    color: 'bg-accent-green/15 text-accent-green/80',
  },
  NEUTRAL: {
    label: 'Neutral',
    description: 'No clear trend or conflicting signals',
    tagline: 'LP\'s natural habitat. Focus on carry optimization, delta-neutral LP, and stable spreads.',
    color: 'bg-surface-hover text-ink-soft',
  },
  WEAK_BEAR: {
    label: 'Weak Bear',
    description: 'Trend down + mixed signals',
    tagline: 'Defensive posture. Reduce borrow, widen LP, increase hedge ratio. No new loops.',
    color: 'bg-red-500/15 text-red-400/80',
  },
  STRONG_BEAR: {
    label: 'Strong Bear',
    description: 'Trend down + sentiment < 0.3 + volume confirming',
    tagline: 'Capital preservation. Exit LP and loops. Full hedge on remaining SOL. Max reserves.',
    color: 'bg-red-500/20 text-red-400',
  },
  VOLATILE: {
    label: 'Volatile',
    description: 'High ATR + rapid regime shifts',
    tagline: 'Widen ranges, reduce sizes, hedge immediately. Do not chase moves.',
    color: 'bg-yellow-400/20 text-yellow-300',
  },
}

// Per-stage per-regime playbook guidance (from §4.1-§4.6)
export type StageName = 'STAKE' | 'LEND' | 'BORROW' | 'LP' | 'HEDGE' | 'LOOP'

interface StageGuidance {
  action: string
  detail: string
  notes?: string
}

export const STAGE_PLAYBOOK: Record<StageName, Record<MarketRegime, StageGuidance>> = {
  STAKE: {
    STRONG_BULL: { action: 'Hold all. Add more if idle SOL.', detail: '100% deployed SOL liquid-staked. Staking compounds positively in bull.' },
    WEAK_BULL:   { action: 'Hold all.', detail: 'Do not add fresh unless reserve is above target.' },
    NEUTRAL:     { action: 'Hold all.', detail: 'Consider consolidating to single LST for simplicity.' },
    WEAK_BEAR:   { action: 'Hold.', detail: 'Do not unstake. Staking accumulates SOL regardless of price. Sell LSTs via DEX if exiting.' },
    STRONG_BEAR: { action: 'Keep staked if long-term.', detail: 'If exiting SOL: sell LSTs via DEX (instant). Do not sell >50% in single tx if >$100K.' },
    VOLATILE:    { action: 'Hold all.', detail: 'VOLATILE is not a reason to unstake.' },
  },
  LEND: {
    STRONG_BULL: { action: 'Maximize lent collateral.', detail: 'Prioritize Kamino (risk score <3, highest TVL). Lend LSTs for dual yield.' },
    WEAK_BULL:   { action: 'Maintain.', detail: 'Accept lower APR if utilization falls. Monitor oracle deviation.' },
    NEUTRAL:     { action: 'Full collateral deployment.', detail: 'Consider lending stablecoins too if reserve USDC above 13%.' },
    WEAK_BEAR:   { action: 'Maintain collateral.', detail: 'Do not add new positions. Keep lent to support HF.' },
    STRONG_BEAR: { action: 'Withdraw only if fully exiting.', detail: 'Otherwise maintain to support health factor.' },
    VOLATILE:    { action: 'Maintain.', detail: 'Do NOT add new collateral (oracle gap risk). Pause borrow mgmt if ATR z>2.0.' },
  },
  BORROW: {
    STRONG_BULL: { action: 'Borrow aggressively. Target HF 1.60.', detail: 'Max LTV ~50%, utilization 69%. Stop increases if borrow APR jumps >5% in 24h.' },
    WEAK_BULL:   { action: 'Target HF 1.70.', detail: 'Max LTV ~47%, utilization 58%. Pre-emptively reduce 10-15% if sentiment drops below 0.40.' },
    NEUTRAL:     { action: 'Maintain HF 1.80.', detail: 'Max LTV ~44%, utilization 58%. Focus on carry optimization, not growth.' },
    WEAK_BEAR:   { action: 'Aggressively reduce. Target HF 2.00.', detail: 'Max LTV ~40%, utilization 48%. Liquidation buffer 30%. Repay at each 5% decline.' },
    STRONG_BEAR: { action: 'Aggressively repay. Target HF 2.20.', detail: 'Max LTV ~36%, utilization 30-35%. Liquidation buffer 40%. Emergency close at HF 1.80.' },
    VOLATILE:    { action: 'Increase HF to 2.00 immediately.', detail: 'Utilization 48%. Do NOT chase upside by increasing borrow in volatile upswing.' },
  },
  LP: {
    STRONG_BULL: { action: 'Enter/expand. ±10-12% range, upward skew.', detail: 'FRR minimum 1.30. Rebalance at 20% of edge. Exit if FRR <1.00. Consider spot over LP if trend confidence >0.70.' },
    WEAK_BULL:   { action: '±18-22% range, slight upward skew.', detail: 'Rebalance at 30% of edge. Exit if FRR <1.10 or trendScore <0.20.' },
    NEUTRAL:     { action: 'Optimal regime for LP. ±18-22% symmetric.', detail: 'Low-vol: tighten to ±12%. Delta-neutral LP works well here. Rebalance at 35% of edge.' },
    WEAK_BEAR:   { action: 'Widen to ±25-30%. No new entry.', detail: 'SR-anchored range. Full hedge required (55%+). Rebalance at 40% of edge. Consider full exit.' },
    STRONG_BEAR: { action: 'EXIT ALL LP IMMEDIATELY.', detail: 'Below-range LP = 100% SOL earning zero fees in downtrend. Remove → swap → repay. Do not re-enter until NEUTRAL confirmed 48h.' },
    VOLATILE:    { action: 'Widen to ±25-30%. Reduce capital 30-50%.', detail: 'No new LP entry. Most conservative rebalance trigger (50% of edge). Min 2h between rebalances.' },
  },
  HEDGE: {
    STRONG_BULL: { action: 'Minimal — 10-20% on LP delta only.', detail: 'Uptrend reduction factor reduces hedge size. Zero hedge only if no LP and no borrowed capital. Max funding 30% APR.' },
    WEAK_BULL:   { action: '25-35% hedge on LP delta.', detail: 'If hedge efficiency <0.5: reduce toward 25% floor. Max funding 20% APR.' },
    NEUTRAL:     { action: '35-50% hedge (delta-neutral LP).', detail: 'Funding typically lower in NEUTRAL. Size perp short to match LP delta (~0.5). Max funding 15% APR.' },
    WEAK_BEAR:   { action: '55-70% on LP + 20-30% on staking.', detail: 'Never reduce below 55%. Break-even funding at 60% hedge: 16.7% APR. Max funding 20% APR.' },
    STRONG_BEAR: { action: '80-100% on ALL remaining SOL exposure.', detail: 'Funding may turn negative (shorts receive payment). Reduce only when trendScore >-0.50.' },
    VOLATILE:    { action: '60-80% hedge IMMEDIATELY.', detail: 'Volatility factor 1.40× above base. Use 7-day avg + 1 std dev for funding estimate. Reduce when ATR z<1.2 for 24h.' },
  },
  LOOP: {
    STRONG_BULL: { action: 'Up to 3 loops. Max leverage 3.0×.', detail: 'Net carry spread >0.50% required. Deleverage if borrow APR >18%. Stress-test: 30% SOL drop must keep HF >1.45.' },
    WEAK_BULL:   { action: 'Max 2 loops. Leverage cap 2.5×.', detail: 'No loop if: effective leverage >2.5× post-loop, stress HF <1.55, or spread <0.5%.' },
    NEUTRAL:     { action: 'Max 2 loops. Leverage cap 2.5×.', detail: 'Stable spreads make loops productive. Same risk constraints as WEAK_BULL.' },
    WEAK_BEAR:   { action: 'Max 1 loop. Close if leverage >1.8×.', detail: 'No new loops permitted. Close remaining loop if net carry spread <0.5%.' },
    STRONG_BEAR: { action: 'CLOSE ALL LOOPS IMMEDIATELY.', detail: 'Sequential unwind innermost to outermost. Maintain HF >1.80 at every step. Complete within 24-48h.' },
    VOLATILE:    { action: 'Max 1 loop. Close all if >48h volatile.', detail: 'Funding cost spikes reduce net carry. Close immediately if net carry turns negative.' },
  },
}

// Per-mode parameter highlights for each stage
interface ModeParams {
  label: string
  conservative: string
  balanced: string
  maxEarn: string
}

export const MODE_PARAMS: Record<StageName, ModeParams[]> = {
  STAKE: [],
  LEND: [],
  BORROW: [
    { label: 'Target HF',  conservative: '2.10', balanced: '1.80', maxEarn: '1.60' },
    { label: 'Max LTV',    conservative: '~40%', balanced: '~47%', maxEarn: '~50%' },
  ],
  LP: [
    { label: 'Range',      conservative: '±25-30%', balanced: '±18-22%', maxEarn: '±10-15%' },
    { label: 'Min FRR',    conservative: '1.50', balanced: '1.30', maxEarn: '1.10' },
  ],
  HEDGE: [
    { label: 'Ratio',      conservative: '55-90%', balanced: '35-70%', maxEarn: '10-45%' },
    { label: 'Max funding', conservative: '15% APR', balanced: '20% APR', maxEarn: '30% APR' },
  ],
  LOOP: [
    { label: 'Max depth',  conservative: '1', balanced: '2', maxEarn: '3' },
    { label: 'Max leverage', conservative: '1.8×', balanced: '2.5×', maxEarn: '3.0×' },
  ],
}

// Key regime parameters (from §6.1 consolidated reference table)
interface RegimeParam { label: string; value: string }

export function getRegimeParams(regime: MarketRegime): RegimeParam[] {
  const table: Record<MarketRegime, RegimeParam[]> = {
    STRONG_BULL: [
      { label: 'Reserve',   value: '8%' },
      { label: 'Max Loop',  value: '3' },
      { label: 'Hedge',     value: '10-20%' },
      { label: 'Kelly',     value: '1.0×' },
      { label: 'Max Fund',  value: '30%' },
      { label: 'LP Entry',  value: 'Allowed' },
    ],
    WEAK_BULL: [
      { label: 'Reserve',   value: '10%' },
      { label: 'Max Loop',  value: '2' },
      { label: 'Hedge',     value: '25-35%' },
      { label: 'Kelly',     value: '0.75×' },
      { label: 'Max Fund',  value: '20%' },
      { label: 'LP Entry',  value: 'Allowed' },
    ],
    NEUTRAL: [
      { label: 'Reserve',   value: '13%' },
      { label: 'Max Loop',  value: '2' },
      { label: 'Hedge',     value: '35-50%' },
      { label: 'Kelly',     value: '0.50×' },
      { label: 'Max Fund',  value: '15%' },
      { label: 'LP Entry',  value: 'Allowed' },
    ],
    WEAK_BEAR: [
      { label: 'Reserve',   value: '18%' },
      { label: 'Max Loop',  value: '1' },
      { label: 'Hedge',     value: '55-70%' },
      { label: 'Kelly',     value: '0.25×' },
      { label: 'Max Fund',  value: '20%' },
      { label: 'LP Entry',  value: 'Blocked' },
    ],
    STRONG_BEAR: [
      { label: 'Reserve',   value: '30%' },
      { label: 'Max Loop',  value: '0' },
      { label: 'Hedge',     value: '80-100%' },
      { label: 'Kelly',     value: '0×' },
      { label: 'Max Fund',  value: 'N/A' },
      { label: 'LP Entry',  value: 'EXIT' },
    ],
    VOLATILE: [
      { label: 'Reserve',   value: '20%' },
      { label: 'Max Loop',  value: '1' },
      { label: 'Hedge',     value: '60-80%' },
      { label: 'Kelly',     value: '0.25×' },
      { label: 'Max Fund',  value: '25%' },
      { label: 'LP Entry',  value: 'Blocked' },
    ],
  }
  return table[regime]
}
